import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'

import { userAtom } from '@root/entities/auth/atoms'
import { UserShort } from '@root/entities/user-short/model'
import { logoutAction } from '@root/features/auth/logout/atoms'
import {
  PortfolioV2Route,
  ProfileRoute,
  WalletManagerRoute,
} from '@root/features/routing/model/app-route'
import { profileBoardAtom } from '@root/features/user/profile/atoms/reatom-profile-boards'
import { ProfileTab } from '@root/features/user/profile/model/profile-tabs'

import { PulseHelp } from './pulse-help'
import { PulseSettings } from './pulse-settings'
import { PulseShortcuts } from './pulse-shortcuts'

/**
 * The reader's own corner of the board.
 *
 * On a wide screen it drops from the avatar; on a phone it comes up from the
 * bottom edge, where a thumb is. Same menu either way — a phone gets the whole
 * of it, not a shortened version, because the things people reach for on a
 * phone are the same ones.
 *
 * It renders its own avatar rather than the app's. This board is mounted
 * outside the app's theme so it can carry its own palette, and a shared
 * component reading the app's tokens gets their light-theme values — the same
 * trap that made a token's initial near-black on near-black.
 */
export const AccountMenu: React.FC = memo(() => {
  const [user] = useAtom(userAtom)
  const [open, setOpen] = useState(false)
  const [modal, setModal] = useState<
    'settings' | 'shortcuts' | 'help' | undefined
  >(undefined)

  const initials = user ? UserShort.initials(user) : ''
  const name = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username
    : 'Signed out'

  return (
    <Root>
      <button
        className="face"
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Your account"
        onClick={() => setOpen(!open)}
      >
        {user?.avatarSrc ? (
          <img src={user.avatarSrc} alt="" />
        ) : (
          <span className="initials">{initials || '?'}</span>
        )}
      </button>

      {open ? (
        <>
          <span className="scrim" onClick={() => setOpen(false)} />
          <div className="sheet" role="menu">
            <div className="who">
              <span className="whoface">
                {user?.avatarSrc ? (
                  <img src={user.avatarSrc} alt="" />
                ) : (
                  <span className="initials">{initials || '?'}</span>
                )}
              </span>
              <span className="whotext">
                <b>{name}</b>
                {user?.username ? <i>@{user.username}</i> : undefined}
              </span>
            </div>

            <AccountMenuItems
              onPick={() => setOpen(false)}
              onOpenModal={setModal}
            />
          </div>
        </>
      ) : undefined}

      {modal === 'settings' ? (
        <PulseSettings onClose={() => setModal(undefined)} />
      ) : undefined}
      {modal === 'shortcuts' ? (
        <PulseShortcuts onClose={() => setModal(undefined)} />
      ) : undefined}
      {modal === 'help' ? (
        <PulseHelp onClose={() => setModal(undefined)} />
      ) : undefined}
    </Root>
  )
})

/**
 * Every entry goes somewhere that exists.
 *
 * Axiom's menu carries a notification centre and a feature-updates page; this
 * app has neither, so neither is drawn. Settings opens the app's own modal,
 * which is mounted above the routes and so is reachable from this board even
 * though the board sits outside the terminal's layout.
 */
const AccountMenuItems: React.FC<{
  onPick: () => void
  onOpenModal: (which: 'settings' | 'shortcuts' | 'help') => void
}> = memo(({ onPick, onOpenModal }) => {
  const navigate = useNavigate()
  const setProfileBoard = useAction(profileBoardAtom)
  const logout = useAction(logoutAction)

  const go = (run: () => void) => () => {
    onPick()
    run()
  }

  return (
    <>
      <div className="group">
        <button
          className="item"
          type="button"
          role="menuitem"
          onClick={go(() => navigate(ProfileRoute.path))}
        >
          <svg className="io" viewBox="0 0 24 24">
            <circle cx="12" cy="8.4" r="3.6" />
            <path d="M4.8 20.2c0-3.7 3.2-5.8 7.2-5.8s7.2 2.1 7.2 5.8" />
          </svg>
          Profile
        </button>

        <button
          className="item"
          type="button"
          role="menuitem"
          onClick={go(() => {
            setProfileBoard(ProfileTab.Referral)
            navigate(ProfileRoute.path)
          })}
        >
          <svg className="io" viewBox="0 0 24 24">
            <circle cx="9.4" cy="8.4" r="3.4" />
            <path d="M3 20c0-3.5 2.9-5.5 6.4-5.5 1.5 0 2.9.4 4 1" />
            <path d="M17.6 14.2v5.2M15 16.8h5.2" />
          </svg>
          Invite friends
        </button>

        <button
          className="item"
          type="button"
          role="menuitem"
          onClick={go(() => navigate(PortfolioV2Route.path))}
        >
          <svg className="io" viewBox="0 0 24 24">
            <path d="M3.4 19.6h17.2" />
            <path d="M6.6 19.6v-6.2M11 19.6V7.4M15.4 19.6v-9M19.8 19.6V4.4" />
          </svg>
          Portfolio
        </button>

        <button
          className="item"
          type="button"
          role="menuitem"
          onClick={go(() => navigate(WalletManagerRoute.path))}
        >
          <svg className="io" viewBox="0 0 24 24">
            <rect x="2.8" y="6" width="18.4" height="13.4" rx="2.6" />
            <path d="M7.4 6V4.8a2.2 2.2 0 0 1 2.2-2.2h4.8a2.2 2.2 0 0 1 2.2 2.2V6" />
          </svg>
          Wallets
        </button>
      </div>

      <div className="group">
        <button
          className="item"
          type="button"
          role="menuitem"
          onClick={go(() => onOpenModal('settings'))}
        >
          <svg className="io" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="3.1" />
            <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-2.87 1.2v.17a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 8 19.4a1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 3.5 14H3.3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 8a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 3.5h.09A2 2 0 0 1 13 3.3v.09a1.7 1.7 0 0 0 1 1.21 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.09a1.7 1.7 0 0 0 1.21 1H20.7a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.21 1Z" />
          </svg>
          Settings
        </button>

        <button
          className="item keys"
          type="button"
          role="menuitem"
          onClick={go(() => onOpenModal('shortcuts'))}
        >
          <svg className="io" viewBox="0 0 24 24">
            <rect x="2.6" y="5.4" width="18.8" height="13.2" rx="2.4" />
            <path d="M7 9.6h.01M11 9.6h.01M15 9.6h.01M8 14h8" />
          </svg>
          Keyboard shortcuts
        </button>

        <button
          className="item"
          type="button"
          role="menuitem"
          onClick={go(() => onOpenModal('help'))}
        >
          <svg className="io" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="8.6" />
            <path d="M9.6 9.4a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.8-.9 1.4v.4" />
            <path d="M12 16.9v.01" />
          </svg>
          Get help
        </button>
      </div>

      <div className="group">
        <button
          className="item out"
          type="button"
          role="menuitem"
          onClick={go(() => logout())}
        >
          <svg className="io" viewBox="0 0 24 24">
            <path d="M9.6 20.4H6.2A2.2 2.2 0 0 1 4 18.2V5.8a2.2 2.2 0 0 1 2.2-2.2h3.4" />
            <path d="M15.4 16.2 19.6 12l-4.2-4.2M19.6 12H9.6" />
          </svg>
          Log out
        </button>
      </div>
    </>
  )
})

const Root = styled.span`
  position: relative;
  display: inline-flex;

  .face {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 9999px;
    overflow: hidden;
    display: grid;
    place-items: center;
    background: var(--chip-hi);
    box-shadow: inset 0 0 0 1px var(--hair);
    cursor: pointer;
  }

  .face:hover {
    box-shadow: inset 0 0 0 1px var(--t5);
  }

  .face img,
  .whoface img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .initials {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.2px;
    color: var(--t2);
  }

  .scrim {
    position: fixed;
    inset: 0;
    z-index: 5;
  }

  .sheet {
    position: absolute;
    z-index: 6;
    top: calc(100% + 8px);
    right: 0;
    width: 244px;
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 8px;
    box-shadow: 0 18px 44px rgba(0, 0, 0, 0.6);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    text-align: left;
  }

  .who {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-bottom: 1px solid var(--line-soft);
  }

  .whoface {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: 9999px;
    overflow: hidden;
    display: grid;
    place-items: center;
    background: var(--chip-hi);
  }

  .whoface .initials {
    font-size: 13px;
  }

  .whotext {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .whotext b {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.1px;
    color: var(--t1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .whotext i {
    font-style: normal;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 11px;
    color: var(--t4);
  }

  .group {
    padding: 6px;
    border-bottom: 1px solid var(--line-soft);
    display: flex;
    flex-direction: column;
  }

  .group:last-of-type {
    border-bottom: 0;
  }

  .item {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 34px;
    padding: 0 8px;
    border-radius: 5px;
    background: none;
    border: 0;
    color: var(--t2);
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
  }

  .item:hover {
    background: var(--chip);
    color: var(--t1);
  }

  .item .io {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.7;
    stroke-linecap: round;
    stroke-linejoin: round;
    color: var(--t4);
  }

  .item:hover .io {
    color: var(--t2);
  }

  .item.out,
  .item.out .io {
    color: var(--down);
  }

  .item.out:hover {
    background: rgba(225, 92, 92, 0.1);
    color: var(--down);
  }

  /* On a phone the menu comes up from the edge a thumb can reach, and takes
     the full width rather than hanging off the corner it was opened from. */
  @container pulse (max-width: 759px) {
    .sheet {
      position: fixed;
      top: auto;
      right: 0;
      left: 0;
      bottom: 0;
      width: auto;
      border-radius: 14px 14px 0 0;
      border-bottom: 0;
      padding-bottom: env(safe-area-inset-bottom, 0px);
    }

    .who {
      padding: 16px 16px 14px;
    }

    .group {
      padding: 6px 10px;
    }

    /* Nothing to learn here without a keyboard. */
    .keys {
      display: none;
    }

    .item {
      height: 46px;
      font-size: 15px;
      gap: 13px;
      padding: 0 10px;
    }

    .item .io {
      width: 19px;
      height: 19px;
    }
  }
`
